import {
  Box,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import axiosClient from "../../api/axiosClient";
import Layout from "../../components/Layout";
import { RoleRequest } from "./RoleRequest";
import { RoleResponse } from "./RoleResponse";
import EditIcon from "@mui/icons-material/Edit";
import { RoleDto } from "./RoleDto";
import { searchGridSx } from "../../utils/responsiveStyles";

export default function Role() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);

  // Dropdown permissions
  const [newPermissions, setPermissions] = useState([]);

  // Search filters
  const [roleName, setRoleName] = useState(null);
  const [permission, setPermission] = useState(null);

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [modalHeader, setModalHeader] = useState("");
  const [newRole, setNewRole] = useState({
    roleName: "",
    description: "",
    permissions: [],
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const clearNewRole = () => {
    setNewRole({
      roleName: "",
      description: "",
      permissions: [],
    });
  };

  const handleModal = (value) => {
    setModalHeader(value);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setErrorMessage("");
    setSuccessMessage("");
    setModalHeader("");
    clearNewRole();
  };

  // Load dropdown permissions
  useEffect(() => {
    axiosClient
      .get("/idm-role/get-permissions-for-role")
      .then((res) => setPermissions(res.data || []))
      .catch((err) => console.error("Dropdown fetch failed", err));
  }, []);

  // Fetch Data
  const fetchData = () => {
    setLoading(true);
    const request = new RoleRequest();
    request.setStartIndex(page * pageSize);
    request.setLimit(pageSize);

    if (sortModel.length > 0) {
      const sort = sortModel[0];
      var backendField =
        {
          id: "id",
          roleName: "role_name",
          description: "description",
        }[sort.field] || sort.field;
      request.setOrderBy(
        `order by ${backendField} ${sort.sort?.toUpperCase()}`
      );
    }

    if (roleName) request.setRoleName(roleName);
    if (permission) request.setPermission(permission);

    axiosClient
      .post("/idm-role/get-all-idm-role", request)
      .then((res) => {
        const roleResponse = new RoleResponse(res.data);
        if (roleResponse?.getOperationResult() && roleResponse.getRoleList()) {
          setRows(roleResponse.getRoleList());
          setRowCount(roleResponse.getCount());
        } else {
          setRows([]);
          setRowCount(0);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, sortModel]);

  // Handle new Role save
  const handleSaveRole = () => {
    setErrorMessage("");
    if (!newRole.roleName || newRole.permissions.length === 0) {
      setErrorMessage("Please fill all required fields. Marked With (*)");
      return;
    }

    if (!newRole.permissions || newRole.permissions.length === 0) {
      setErrorMessage("Please fill all required fields. Marked With (*)");
      return;
    }

    axiosClient
      .post("/idm-role/save", newRole) // ✅ replace with your actual endpoint
      .then((res) => {
        if (res != null && res.data != null && res.data.operationResult) {
          if (newRole.id != null) {
            setSuccessMessage("Role Edited Successfully");
          } else {
            setSuccessMessage("Role Saved Successfully");
          }
          setModalHeader("Create New Role");
          clearNewRole();
          fetchData(); // refresh table
        } else {
          const msg = res?.data?.errorMsg || "Role creation failed.";
          setErrorMessage(msg);
        }
      })
      .catch((err) => {
        console.error("Role creation failed", err);
        const msg = err.response?.data?.message || "Role creation failed.";
        setErrorMessage(msg);
      });
  };

  // Handle Edit button click
  const handleEditRole = (id) => {
    console.log("Edit role with ID:", id);
    setLoading(true);
    axiosClient
      .get("/idm-role/get-by-id/" + Number(id))
      .then((res) => {
        const role = new RoleDto(res.data);
        console.log("role: " + JSON.stringify(role));
        if (role != null && role.id != null && role.id > 0) {
          setNewRole({
            id: role.getId(),
            roleName: role.getRoleName(),
            description: role.getDescription(),
            permissions: role.getPermissions(),
          });
        } else {
          setNewRole({
            id: null,
            roleName: "",
            description: "",
            permissions: [],
          });
          setErrorMessage("Couldn't Get Role Data");
        }
        setModalHeader("Edit Role");
        setOpenModal(true);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "roleName", headerName: "Role Name", flex: 1 },
    { field: "description", headerName: "Descrption", flex: 1 },
    {
      field: "permissionsStr",
      sortable: false,
      headerName: "Permissions",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 90,
      renderCell: (params) => (
        <Tooltip title="Edit">
          <IconButton
            sx={{
              backgroundColor: "#1976d2", // blue background
              color: "white", // make icon white
              "&:hover": {
                backgroundColor: "#115293", // darker on hover
              },
            }}
            size="small"
            onClick={() => handleEditRole(params.row.id)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  //handle permissions for add/edit
  const handlePermissions = (selectedValues) => {
    // Convert values to an array of objects like { id: value }
    const values = selectedValues.map((v) => ({ id: Number(v) }));

    setNewRole((prev) => ({
      ...prev,
      permissions: values,
    }));
  };

  return (
    <Layout title="Role Management">
      {/* 🔹 Top bar with Add button */}
      <Grid
        container
        justifyContent={{ xs: "stretch", sm: "flex-end" }}
        sx={{ mb: 2 }}
      >
        <Button
          variant="contained"
          color="success"
          onClick={() => handleModal("Create New Role")}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          + Add Role
        </Button>
      </Grid>

      {/* 🔹 Search Form */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Grid container sx={searchGridSx}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Role Name"
              type="text"
              value={roleName || ""}
              onChange={(e) => setRoleName(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Permission"
              value={permission || ""}
              onChange={(e) => setPermission(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {newPermissions.map((dt, i) => (
                <MenuItem key={i} value={dt.id}>
                  {dt.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
              onClick={fetchData}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowHeight={() => "auto"}
          sx={{
            minWidth: { xs: 860, sm: "100%" },
            bgcolor: "white",
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center", // center vertically
              whiteSpace: "normal", // allow multi-line text
              lineHeight: "1.4rem", // smoother line spacing for wrapped text
              paddingTop: "8px",
              paddingBottom: "8px",
            },
            "& .MuiDataGrid-columnHeaders": {
              alignItems: "center",
            },
          }}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          sortingOrder={["asc", "desc"]}
          loading={loading}
          pageSizeOptions={[5, 10, 20, 50]}
          autoHeight
        />
      </Box>

      {/* 🔹 Add Role Modal */}
      <Dialog
        open={openModal}
        onClose={() => handleModalClose()}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { m: { xs: 1.5, sm: 3 }, width: "100%" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#1565C0",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {modalHeader}
        </DialogTitle>
        <DialogContent dividers>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {/* Top-level Grid */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Row 1 */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Role Name *"
                fullWidth
                inputProps={{ maxLength: 50 }}
                value={newRole.roleName}
                onChange={(e) =>
                  setNewRole({ ...newRole, roleName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Description"
                inputProps={{ maxLength: 30 }}
                fullWidth
                value={newRole.description}
                onChange={(e) =>
                  setNewRole({ ...newRole, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Permission *"
                sx={{ minWidth: { sm: 210 } }}
                fullWidth
                value={(newRole.permissions || []).map((p) => p.id)} // map to array of ids
                onChange={(e) => handlePermissions(e.target.value)}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) =>
                    newPermissions
                      .filter((p) => selected.includes(p.id))
                      .map((p) => p.name)
                      .join(", "),
                }}
              >
                {newPermissions.map((r) => {
                  const isSelected =
                    Array.isArray(newRole.permissions) &&
                    newRole.permissions.some((p) => p.id === r.id); // ✅ compare by id

                  return (
                    <MenuItem
                      key={r.id}
                      value={r.id}
                      sx={{
                        backgroundColor: isSelected
                          ? "rgba(25, 118, 210, 0.15)"
                          : "inherit",
                        color: isSelected ? "#1565C0" : "inherit",
                        fontWeight: isSelected ? 600 : 400,
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.25)",
                        },
                      }}
                    >
                      {r.name}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleModalClose()}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRole}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
